<template>
  <ModalBase :show-modal="showModal" :auto-h="true" :narrow="true" @close-modal="close">
    <template v-slot:titlebar>
      <TitleBar variant-class="title-modal">
        <template v-slot:title>
          <div class="premium-badge--inline">
            <div v-if="isEdit" style="display: flex; align-items: center; gap: 1rem;">
              {{ $gettext('Edit product') }}
              <GkBadge 
                icon="fa-solid fa-crown"
                :text="$gettext('Premium')"
                variant="base"
                type="premium"
              />
            </div>
            <div v-else style="display: flex; align-items: center; gap: 1rem;">
              {{ $gettext('Add new product') }}
              <GkBadge 
                icon="fa-solid fa-crown"
                :text="$gettext('Premium')"
                variant="base"
                type="premium"
              />
            </div>
          </div>
        </template>
      </TitleBar>
    </template>
    <template v-slot:maincontent>
      <AddEditProduct
        ref="add-edit-product-form"
        :product="product"
        :initial-group-id="initialGroupId"
        :allow-group-creation="allowGroupCreation"
        :product-name="productName"
        @product-created="onProductCreated"
        @product-updated="onProductUpdated"
        @group-created="$emit('group-created')"
      />
      <div v-if="isEdit && productInvoices && productInvoices.length > 0" class="usage-box">
        <div class="usage-box--title">
          <h3>{{ $gettext('Used on these invoice drafts') }}</h3>
          <p class="usage-box--hint">{{ $gettext('Updating this product won\'t change current invoices or drafts. Edits must be made manually on each draft.') }}</p>
        </div>

        <ul class="usage-box--list">
          <li v-for="invoice in productInvoices" :key="invoice.id">
            <div>Invoice {{ invoice.number }}</div>
            <div style="display: flex; gap: 0.5rem; align-items: center;">
              <StatusBadge :display-status="getInvoiceStatus(invoice.status)" />
              <GekkoButton
                @click="openEditInvoice(invoice)"
                :text="$gettext('Edit')"
                :icon="'fa-solid fa-pen-to-square'"
                variant="link"
                class="text-blue"
                size="sm"
              />
            </div>
          </li>
        </ul>
      </div>
    </template>
    <template v-slot:actions>
      <div class="d-flex justify-content-between">
        <GekkoButton 
          @click="close()"
          :text="$gettext('Cancel')"
          variant="light"
        />
        <GekkoButton 
          v-if="isEdit"
          @click="$refs['add-edit-product-form'].save()"
          :text="$gettext('Save')"
          class="position-relative"
          :loading="isWaitingForResponse"
        />
        <GekkoButton
          v-else
          @click="$refs['add-edit-product-form'].save()"
          :text="$gettext('Add product')"
          class="position-relative"
          :loading="isWaitingForResponse"
        />
      </div>
    </template>
  </ModalBase>
</template>

<script>
import { mapGetters, mapMutations } from "vuex";

import ProductGroupService from "@/store/services/productgroup.service.js";
import ProductService from "@/store/services/product.service.js";

import ModalBase from "@/components/base_components/ModalBase";
import TitleBar from "@/components/TitleBar";
import GekkoButton from "@/components/GekkoButton";
import StatusBadge from "@/components/invoices/ui/StatusBadge"
import AddEditProduct from "@/components/products/AddEditProduct.vue"

export default {
  name: 'AddEditProductModal',
  components: {
    GekkoButton,
    ModalBase,
    TitleBar,
    StatusBadge,
    AddEditProduct
  },
  props: {
    product: {
      type: Object,
      default: () => {}
    },
    initialGroupId: {
      type: Number,
      default: null
    },
    allowGroupCreation: {
      type: Boolean,
      default: false
    },
    productName: {
      type: String,
      default: null
    }
  },
  data () {
    return {
      showModal: true,
      productInvoices: []
    }
  },
  computed: {
    ...mapGetters({
      currentCompany: "main/getCurrentCompany",
      isWaitingForResponse: 'main/isWaitingForResponse'
    }),
    isEdit () {
      return this.product?.id
    }
  },
  created () {
    if (this.isEdit) {
      this.getProductUsage()
    }
  },
  methods: {
    ...mapMutations({
      setWaitingForResponse: 'main/SET_WAITING_FOR_RESPONSE'
    }),
    getProductUsage () {
      ProductService.getUsage(this.product.id, { invoice_status: 'd' }).then((response) => {
        this.productInvoices = response.data
      })
    },
    onProductCreated(product) {
      this.$emit('product-created', product)
      this.close()
    },
    onProductUpdated(productId) {
      this.$emit('product-updated', false)
      this.close()
    },
    close () {
      this.$emit('close', false)
      document.body.classList.remove('modal-on')
    },
    getInvoiceStatus (status) {
      let badge_status = { detailed_status: 'draft', display_status: this.$gettext('draft') }
      if (status === 'recurrent') {
        badge_status = { detailed_status: 'recurrent', display_status: this.$gettext('recurrent') }
      }
      return badge_status
    },
    openEditInvoice (invoice) {
      // forces a full reload of the current route, otherwise will load gekko view components inside settings
      if (invoice.status === 'd') {
        this.$router.push({ name: 'InvoiceEdit', params: { invoiceId: invoice.id } }).then(() => {
          this.$router.go()
        }).catch(() => {})
      }
      if (invoice.status === 'recurrent') {
        this.$router.push({ name: 'InvoiceViewRecurrent', params: { invoiceId: invoice.recurrentinvoice } }).then(() => {
          this.$router.go()
        }).catch(() => {})
      }
      
    },
  }
}
</script>
<style lang="scss" scoped>
.usage-box {
  padding: 1rem 0;
  border-top: 1px solid $colorBorderDash;

  &--hint {
    font-size: 1rem;
    color: $colorTextSemi;
  }
  &--list {
    margin: 0;
    padding: 0;
    font-size: 14px;
    max-height: 300px;
    overflow-y: scroll;

    background: $colorTableOddBg;
    border: 1px solid $colorBorderDash;
    border-radius: $radiusLg;

    li {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.5rem;
      padding: 0.5rem 1rem;
      &:not(:last-child) {
        border-bottom: 1px solid $colorBorderDash;
      }
      &:hover {
        background: $colorBlue50;
        box-shadow: $tableHoverShadow;
        border-color: transparent;
      }
    }
  }
}
</style>
